import MailEvent from '@/utils/domain/constants/enumerator/MailEvent';

/**
 * Constants for mail event mappings
 */
const MAIL_EVENT_MAP = Object.freeze({
  0: 'processed',
  1: 'dropped',
  2: 'delivered',
  3: 'bounce',
  4: 'deferred',
  5: 'open',
  6: 'click',
  7: 'unsubscribe',
  8: 'resubscribe',
  9: 'spamReport',
});

/**
 * Default values for fallback scenarios
 */
const DEFAULTS = Object.freeze({
  COLOR: 'green',
  STATUS: {
    color: 'default',
    statusText: 'global.email.status.unknown',
  },
});

/**
 * Utility functions for mail events
 */
export const MailEventUtils = {
  /**
   * Get color by mailEvent number
   * @param {number} mailEvent - The mail event number
   * @returns {string} The color for the event
   */
  getColorByEvent: (mailEvent) => {
    if (mailEvent === null || mailEvent === undefined) {
      return DEFAULTS.COLOR;
    }

    const eventId = MAIL_EVENT_MAP[mailEvent];
    if (!eventId) {
      return DEFAULTS.COLOR;
    }

    const eventConfig = Object.values(MailEventConfig).find((config) => config.id === eventId);

    return eventConfig?.color || DEFAULTS.COLOR;
  },

  /**
   * Get translation key by mailEvent number
   * @param {number} mailEvent - The mail event number
   * @returns {string} The translation key for the event
   */
  getTranslationKeyByEvent: (mailEvent) => {
    if (mailEvent === null || mailEvent === undefined) {
      return 'global.emailEvent.unknown';
    }

    const eventId = MAIL_EVENT_MAP[mailEvent];
    if (!eventId) {
      return 'global.emailEvent.unknown';
    }

    return `global.emailEvent.${eventId}`;
  },

  /**
   * Get all options for dropdowns
   * @returns {Array} Array of options with value and label
   */
  getEventOptions: () => {
    return Object.values(MailEventConfig).map((event) => ({
      value: event.value,
      label: event.translationKey,
    }));
  },

  /**
   * Get status configuration by response code
   * @param {string|number} responseCode - The HTTP response code
   * @returns {Object} Status configuration with color and statusText
   */
  getStatusByResponseCode: (responseCode) => {
    if (!responseCode) {
      return DEFAULTS.STATUS;
    }

    const code = parseInt(responseCode, 10);
    if (isNaN(code)) {
      return DEFAULTS.STATUS;
    }

    return EmailStatusConfig[code] || DEFAULTS.STATUS;
  },

  /**
   * Format status label as "code - translation" (e.g. "400 - Error")
   * @param {string|number} responseCode - The HTTP response code
   * @param {Function} formatMessage - i18n formatter ({ id }) => string
   * @returns {string} Formatted status label
   */
  formatStatusLabel: (responseCode, formatMessage) => {
    const status = MailEventUtils.getStatusByResponseCode(responseCode);
    const label = formatMessage({ id: status.statusText });
    const code = parseInt(responseCode, 10);

    if (!isNaN(code)) {
      return `${code} - ${label}`;
    }

    return label;
  },

  /**
   * Check if a mail event is valid
   * @param {number} mailEvent - The mail event number
   * @returns {boolean} True if valid, false otherwise
   */
  isValidMailEvent: (mailEvent) => {
    return (
      mailEvent !== null && mailEvent !== undefined && MAIL_EVENT_MAP.hasOwnProperty(mailEvent)
    );
  },
};

/**
 * Mail Events Configuration
 * Combines color mapping and translation keys for email events
 */
export const MailEventConfig = Object.freeze({
  [MailEvent.Processed]: {
    id: 'processed',
    color: 'green',
    translationKey: 'global.emailEvent.processed',
    value: 'Processed',
  },
  [MailEvent.Dropped]: {
    id: 'dropped',
    color: 'red',
    translationKey: 'global.emailEvent.dropped',
    value: 'Dropped',
  },
  [MailEvent.Delivered]: {
    id: 'delivered',
    color: 'green',
    translationKey: 'global.emailEvent.delivered',
    value: 'Delivered',
  },
  [MailEvent.Bounce]: {
    id: 'bounce',
    color: 'red',
    translationKey: 'global.emailEvent.bounce',
    value: 'Bounce',
  },
  [MailEvent.Deferred]: {
    id: 'deferred',
    color: 'yellow',
    translationKey: 'global.emailEvent.deferred',
    value: 'Deferred',
  },
  [MailEvent.Open]: {
    id: 'open',
    color: 'green',
    translationKey: 'global.emailEvent.open',
    value: 'Open',
  },
  [MailEvent.Click]: {
    id: 'click',
    color: 'green',
    translationKey: 'global.emailEvent.click',
    value: 'Click',
  },
  [MailEvent.Unsubscribe]: {
    id: 'unsubscribe',
    color: 'red',
    translationKey: 'global.emailEvent.unsubscribe',
    value: 'Unsubscribe',
  },
  [MailEvent.Resubscribe]: {
    id: 'resubscribe',
    color: 'green',
    translationKey: 'global.emailEvent.resubscribe',
    value: 'Resubscribe',
  },
  [MailEvent.SpamReport]: {
    id: 'spamReport',
    color: 'red',
    translationKey: 'global.emailEvent.spamReport',
    value: 'SpamReport',
  },
});

/**
 * Email Message Types Configuration
 */
export const EmailMessageTypes = Object.freeze([
  {
    value: 'Unknown',
    label: 'global.emailType.unknown',
  },
  {
    value: 'ProcessInstancePendingApproval',
    label: 'global.emailType.processInstancePendingApproval',
  },
  {
    value: 'ProcessInstancePendingCompleteInformation',
    label: 'global.emailType.processInstancePendingCompleteInformation',
  },
  {
    value: 'ProcessInstancePendingTaskCompletion',
    label: 'global.emailType.processInstancePendingTaskCompletion',
  },
  {
    value: 'ProcessInstancePendingSignature',
    label: 'global.emailType.processInstancePendingSignature',
  },
  {
    value: 'ProcessInstanceApprovalSecurityCode',
    label: 'global.emailType.processInstanceApprovalSecurityCode',
  },
  {
    value: 'ProcessInstanceActionSendEmail',
    label: 'global.emailType.processInstanceActionSendEmail',
  },
  {
    value: 'ProcessInstanceNewCommentNotification',
    label: 'global.emailType.processInstanceNewCommentNotification',
  },
  {
    value: 'ProcessInstanceScheduledExecutionNotification',
    label: 'global.emailType.processInstanceScheduledExecutionNotification',
  },
  {
    value: 'ProcessInstanceApprovedNotification',
    label: 'global.emailType.processInstanceApprovedNotification',
  },
  {
    value: 'ProcessInstanceRejectedNotification',
    label: 'global.emailType.processInstanceRejectedNotification',
  },
  {
    value: 'ProcessInstanceCanceledNotification',
    label: 'global.emailType.processInstanceCanceledNotification',
  },
  {
    value: 'ProcessInstanceVoidedNotification',
    label: 'global.emailType.processInstanceVoidedNotification',
  },
  {
    value: 'ProcessInstanceWithErrorsNotification',
    label: 'global.emailType.processInstanceWithErrorsNotification',
  },
  {
    value: 'UserAccountConfirmation',
    label: 'global.emailType.userAccountConfirmation',
  },
  {
    value: 'UserAccountWelcomeMessage',
    label: 'global.emailType.userAccountWelcomeMessage',
  },
  {
    value: 'UserAccountResetPassword',
    label: 'global.emailType.userAccountResetPassword',
  },
  {
    value: 'UserAccountTwoFactorValidationCode',
    label: 'global.emailType.userAccountTwoFactorValidationCode',
  },
  {
    value: 'UserAccountSecurityAlertNewLogin',
    label: 'global.emailType.userAccountSecurityAlertNewLogin',
  },
  {
    value: 'InternalTaskErrorNotification',
    label: 'global.emailType.internalTaskErrorNotification',
  },
  {
    value: 'InternalTaskEntityFileReadyToDownloadNotification',
    label: 'global.emailType.internalTaskEntityFileReadyToDownloadNotification',
  },
  {
    value: 'InternalTaskEntityFileImportFinishNotification',
    label: 'global.emailType.internalTaskEntityFileImportFinishNotification',
  },
  {
    value: 'FormApiFieldApiNotificationWithErrors',
    label: 'global.emailType.formFieldApiWithErrorsNotification',
  },
  {
    value: 'MaintenanceCompanyDeactivationNotice',
    label: 'global.emailType.maintenanceCompanyDeactivationNotification',
  },
  {
    value: 'RequestTaskReassigned',
    label: 'global.emailType.processInstanceReassigned',
  },
  {
    value: 'InternalTaskNotificationCompleted',
    label: 'global.emailType.systemReportInternalTaskCompletedNotification',
  },
  {
    value: 'AssignedTask',
    label: 'global.emailType.taskAssigned',
  },
  {
    value: 'InternalTaskDynamicEntityImportCompleted',
    label: 'global.emailType.internalTaskDynamicEntityImportCompleted',
  },
  {
    value: 'AgentEmailMessage',
    label: 'global.emailType.agentEmailMessage',
  },
  {
    value: 'InternalTaskListFileReadyToDownloadNotification',
    label: 'global.emailType.internalTaskListFileReadyToDownloadNotification',
  },
  {
    value: 'AIBillingBudgetAlertNotification',
    label: 'global.emailType.aiBillingBudgetAlertNotification',
  },
  {
    value: 'AIBillingMonthlyCreditGrantedNotification',
    label: 'global.emailType.aiBillingMonthlyCreditGrantedNotification',
  },
]);

/**
 * Email Status Configuration
 * Maps HTTP response codes to status configurations
 */
export const EmailStatusConfig = Object.freeze({
  202: {
    color: 'approved',
    statusText: 'global.email.status.sent',
  },
  400: {
    color: 'rejected',
    statusText: 'global.email.status.badRequest',
  },
  401: {
    color: 'rejected',
    statusText: 'global.email.status.unauthorized',
  },
  403: {
    color: 'rejected',
    statusText: 'global.email.status.forbidden',
  },
  404: {
    color: 'rejected',
    statusText: 'global.email.status.notFound',
  },
  413: {
    color: 'rejected',
    statusText: 'global.email.status.payloadTooLarge',
  },
  429: {
    color: 'rejected',
    statusText: 'global.email.status.tooManyRequests',
  },
  500: {
    color: 'execution',
    statusText: 'global.email.status.internalServerError',
  },
  502: {
    color: 'execution',
    statusText: 'global.email.status.badGateway',
  },
  503: {
    color: 'execution',
    statusText: 'global.email.status.serviceUnavailable',
  },
  504: {
    color: 'execution',
    statusText: 'global.email.status.gatewayTimeout',
  },
});
